import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Chip, DChip, DataTable, Icon, Modal, Pane, PaneHead, PhoneScreen, PickerChip, TabBar,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { toast } from 'sonner';
import { useAiAffordance } from '@/config/aiCapabilities';
import {
  expiryLabel,
  useOrders,
  useShelf,
  useSuppliers,
  type ShelfItem,
  type Supplier,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · Receive — `g_receive` (f4). THE INVOICE, ITS LINES, THE LOT.
 *
 * ONE INVOICE POSTS WHOLE OR NOT AT ALL. The board's button counts the lines
 * ("✓ Receive 2 lines") because a delivery is one event; half of it on the
 * shelf is worse than none of it, since nothing afterwards says which half. So
 * every line goes in one request and the server writes them in one
 * transaction.
 *
 * EXPIRY IS REQUIRED FOR A BATCH-TRACKED LINE — the board's own footnote, and
 * the rule this screen exists to enforce. An undated lot is invisible to FEFO
 * and to every expiry warning built on it, so the receive button stays down
 * until each batch-tracked line carries the date on its box. The server checks
 * it again, because a rule only enforced in a form is not a rule.
 *
 * PRE-FILLED FROM AN ORDER (f4b). `?order=<id>` arrives from a confirmed
 * order's Receive button: its lines become these lines, with the quantities
 * that were ordered and the supplier and invoice already filled, because
 * nothing on a delivery should be typed twice. What still has to be typed is
 * what only the box can say — the batch code and the date printed on it.
 *
 * Receiving against an order settles it: every line met closes the order, and
 * a part-delivery leaves it confirmed with the rest outstanding.
 *
 * THE INVOICE SCAN is an offer: a photo or PDF is read into these same lines
 * for the person to check against the paper. With AI off, or a read that
 * fails, nothing changes — the form is typed as it always was.
 */

interface Line {
  key: string;
  item: ShelfItem;
  quantity: string;
  lotCode: string;
  /** `YYYY-MM-DD` — the date printed on the box. */
  expiresOn: string;
  /** Major units as typed — "8.50". Converted at the boundary. */
  unitCost: string;
  note: string;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

let seq = 0;

export function StockReceivePage() {
  const go = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('order');
  /* `d=lineId:qty,…&n=note` — the delivery check on the order's pane: what actually came, and why a line is short. */
  const delivered = new Map((params.get('d') ?? '').split(',').filter(Boolean).map((x) => x.split(':') as [string, string]));
  const shortNote = params.get('n') ?? '';
  const qc = useQueryClient();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');

  const { data: shelf } = useShelf();
  const items = useMemo(() => [...(shelf ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [shelf]);

  const { data: suppliers } = useSuppliers();
  /* Only asked for when an order is being received against. */
  const { data: orders } = useOrders(false);
  const order = orderId ? (orders ?? []).find((o) => o.id === orderId) : undefined;

  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [pickedKey, setPicked] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [scan, setScan] = useState('');
  const [reading, setReading] = useState(false);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const invoiceScan = useAiAffordance('ocr_attachments');

  usePageMeta({ title: 'Receive' });

  /* THE ORDER'S LINES BECOME THIS INVOICE'S, ONCE. Seeded rather than derived,
     because from here on they are the delivery's — what arrived is often not
     what was ordered, and the person receiving must be able to say so. */
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!order || seeded.current === order.id || !items.length) return;
    seeded.current = order.id;
    setLines(
      order.lines
        .map((l) => {
          const item = items.find((i) => i.id === l.itemId);
          if (!item) return null;
          const outstanding = Math.max(0, l.quantity - l.receivedQuantity);
          const got = delivered.has(l.id) ? Number(delivered.get(l.id)) : null;
          return {
            key: `l${(seq += 1)}`,
            item,
            quantity: String(got !== null ? got : outstanding || l.quantity),
            lotCode: '',
            expiresOn: '',
            unitCost: l.unitCostMinor != null ? (l.unitCostMinor / 100).toFixed(2) : '',
            note: got !== null && got < outstanding && shortNote ? `short — ${shortNote}` : '',
          };
        })
        .filter((l): l is Line => !!l),
    );
    if (order.supplierId) setSupplierId(order.supplierId);
    else if (order.supplierName) setSupplierName(order.supplierName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, items]);

  const picked = lines.find((l) => l.key === pickedKey) ?? null;
  const supplier = (suppliers ?? []).find((s) => s.id === supplierId) ?? null;

  /** A batch-tracked line without its date cannot go. */
  const undated = lines.filter((l) => l.item.batchTracked && !l.expiresOn);
  const uncounted = lines.filter((l) => !(Number(l.quantity) > 0));
  const ready = lines.length > 0 && undated.length === 0 && uncounted.length === 0;

  const patch = (key: string, next: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...next } : l)));

  const addItem = (item: ShelfItem) => {
    const key = `l${(seq += 1)}`;
    setLines((ls) => [
      ...ls,
      { key, item, quantity: '', lotCode: '', expiresOn: '', unitCost: '', note: '' },
    ]);
    setPicked(key);
    setAdding(false);
  };

  /** Read a supplier invoice into the form — a suggestion the person confirms. */
  const scanInvoice = async (file: File | undefined) => {
    if (!file) return;
    setReading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await apiClient.post('/stock/receive/scan', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = unwrap<{
        supplierName: string | null;
        invoiceNumber: string | null;
        lines: Array<{
          description: string;
          quantity: number | null;
          unitPrice: number | null;
          lotCode: string | null;
          expiresOn: string | null;
          itemId: string | null;
        }>;
      }>(res.data);
      const made: Line[] = [];
      const missed: string[] = [];
      for (const l of r.lines) {
        const item = l.itemId ? items.find((i) => i.id === l.itemId) : undefined;
        if (!item) {
          missed.push(l.description);
          continue;
        }
        made.push({
          key: `l${(seq += 1)}`,
          item,
          quantity: l.quantity ? String(l.quantity) : '',
          lotCode: l.lotCode ?? '',
          expiresOn: l.expiresOn ?? '',
          unitCost: l.unitPrice != null ? l.unitPrice.toFixed(2) : '',
          note: '',
        });
      }
      setLines((ls) => [...ls, ...made]);
      setUnmatched(missed);
      if (r.invoiceNumber && !reference) setReference(r.invoiceNumber);
      const sup = r.supplierName
        ? (suppliers ?? []).find((s) => s.name.toLowerCase() === r.supplierName!.toLowerCase())
        : undefined;
      if (sup && !supplierId) setSupplierId(sup.id);
      else if (r.supplierName && !supplierId && !supplierName) setSupplierName(r.supplierName);
      toast.success(
        `Read ${r.lines.length} line${r.lines.length === 1 ? '' : 's'} — check each against the paper${missed.length ? ` · ${missed.length} not in the catalog` : ''}.`,
      );
    } catch {
      toast.error('The invoice could not be read — type the lines in.');
    } finally {
      setReading(false);
    }
  };

  const scanZone = invoiceScan.show ? (
    <label
      className="card card-ghost"
      style={{ display: 'block', cursor: invoiceScan.enabled && !reading ? 'pointer' : 'default', marginBottom: 10 }}
      title={invoiceScan.message ?? undefined}
    >
      <span className="t-sec brand w6">
        <Icon name="camera" className="ic-inline" />{' '}
        {reading ? 'Reading the invoice…' : 'Upload or drop the supplier invoice'}
      </span>
      <span className="t-cap mut"> — the lines fill in; you check them against the paper</span>
      <input
        type="file"
        accept="image/*,application/pdf"
        hidden
        disabled={!invoiceScan.enabled || reading}
        aria-label="Supplier invoice"
        onChange={(e) => {
          void scanInvoice(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </label>
  ) : null;
  const unmatchedNote = unmatched.length ? (
    <Card style={{ borderColor: 'var(--warning-600)' }}>
      <p className="t-sec">Not in the catalog — add these by hand: {unmatched.join(' · ')}</p>
    </Card>
  ) : null;

  const receive = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/stock/receive', {
        ...(orderId ? { orderId } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...(!supplierId && supplierName.trim() ? { supplierName: supplierName.trim() } : {}),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        lines: lines.map((l) => ({
          itemId: l.item.id,
          quantity: Number(l.quantity),
          ...(l.lotCode.trim() ? { lotCode: l.lotCode.trim() } : {}),
          ...(l.expiresOn ? { expiresOn: l.expiresOn } : {}),
          ...(l.unitCost.trim() ? { unitCostMinor: Math.round(Number(l.unitCost) * 100) } : {}),
          ...(l.note.trim() ? { note: l.note.trim() } : {}),
        })),
      });
      return unwrap<{ lines: number }>(res.data);
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['stock'] });
      toast.success(
        `${data?.lines ?? lines.length} line${(data?.lines ?? lines.length) === 1 ? '' : 's'} on the ${branchName} shelf`,
      );
      go(order ? '/stock-manager/orders' : '/stock-manager/shelf');
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'The delivery was not recorded. Nothing has been changed.';
      toast.error(String(msg));
    },
  });


  const tableRows: TableRow[] = lines.map((l) => ({
    id: l.key,
    cells: {
      line: (
        <div>
          <p className="t-strong">{l.item.name}</p>
          <p className="t-cap mut">
            {l.item.unit || l.item.category || 'item'}
            {l.item.batchTracked ? ' · batch-tracked' : ' · no batches'}
            {l.item.inStock > 0 ? ` · ${l.item.inStock} on the shelf` : ''}
          </p>
        </div>
      ),
      qty: (
        <input
          className="fld"
          type="number"
          min={1}
          inputMode="numeric"
          aria-label={`How many ${l.item.name}`}
          value={l.quantity}
          onChange={(e) => patch(l.key, { quantity: e.target.value })}
        />
      ),
      batch: l.item.batchTracked ? (
        <input
          className="fld"
          aria-label={`Batch code for ${l.item.name}`}
          placeholder="code"
          value={l.lotCode}
          onChange={(e) => patch(l.key, { lotCode: e.target.value })}
        />
      ) : (
        <span className="mut">—</span>
      ),
      expiry: l.item.batchTracked ? (
        <input
          className="fld"
          type="date"
          aria-label={`Expiry for ${l.item.name}`}
          value={l.expiresOn}
          onChange={(e) => patch(l.key, { expiresOn: e.target.value })}
        />
      ) : (
        <span className="mut">n/a</span>
      ),
      cost: (
        <input
          className="fld num"
          inputMode="decimal"
          aria-label={`Unit cost for ${l.item.name}`}
          placeholder="—"
          value={l.unitCost}
          onChange={(e) => patch(l.key, { unitCost: e.target.value })}
        />
      ),
    },
  }));

  /* `g_receive` — the two ways in: a blank invoice typed from the paper, or a
     confirmed order whose lines are already here. */
  const openOrders = (orders ?? []).filter((o) => o.status === 'confirmed' || o.status === 'ordered');
  const [way, setWay] = useState<'blank' | 'order'>(orderId ? 'order' : 'blank');

  const orderPicker = (
    <Card className="mb8">
      {openOrders.length ? (
        openOrders.map((o) => (
          <button key={o.id} type="button" className="card card-btn" style={{ margin: '0 0 8px' }}
            onClick={() => go(`/stock-manager/receive?order=${o.id}`)}>
            <p className="t-strong">#{o.number} · {o.supplierName ?? 'supplier'}</p>
            <p className="t-cap mut">{o.lines.length} line{o.lines.length === 1 ? '' : 's'} · {o.status}</p>
          </button>
        ))
      ) : (
        <p className="t-sec mut">No order is waiting on a delivery. Send one from Orders and it lands here.</p>
      )}
    </Card>
  );

  /* `g_k4` — what the phone shows of the same delivery: each line a card with
     its figures and a pencil that opens it, then the one verb. */
  const phoneBody = (
    <>
      {scanZone}
      {unmatchedNote}
      <button type="button" className="card card-btn t-body" style={{ display: 'flex', gap: 10, alignItems: 'center', borderRadius: 999 }}
        onClick={() => setAdding(true)}>
        <Icon name="plus" /> Add manually
      </button>
      {openOrders.length ? (
        <button type="button" className="card card-btn t-body" style={{ display: 'flex', gap: 10, alignItems: 'center', borderRadius: 999 }}
          onClick={() => setWay('order')}>
          <Icon name="clipboard-list" /> From an order
        </button>
      ) : null}
      {way === 'order' ? orderPicker : null}
      {order ? (
        <p className="t-sec brand mb8">
          Pre-filled from #{order.number}
          {' · '}
          <button type="button" className="lnk" onClick={() => go('/stock-manager/orders')}>Open the order</button>
        </p>
      ) : null}
      <Card>
        <p className="t-ovl mut">Supplier</p>
        {suppliers?.length ? (
          <p className="mt4">
            <PickerChip
              label={`${supplier?.name ?? 'Pick a supplier'} ▾`}
              ariaLabel="Supplier"
              kind={supplierId ? 'brand' : 'outline'}
              value={supplierId}
              onChange={setSupplierId}
              options={[
                { value: '', label: 'Not from a listed supplier' },
                ...suppliers.map((s) => ({ value: s.id, label: s.name, hint: s.contactName ?? s.contactPhone ?? undefined })),
              ]}
            />
          </p>
        ) : (
          <input className="fld t-strong mt4" aria-label="Supplier" placeholder="who it came from"
            value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
        )}
        <label className="t-ovl mut mt12" htmlFor="rcv-ref-p" style={{ display: 'block' }}>Invoice</label>
        <input id="rcv-ref-p" className="fld t-strong mt4" placeholder="invoice number"
          value={reference} onChange={(e) => setReference(e.target.value)} />
      </Card>
      {lines.length ? (
        <>
          <p className="t-sec mut mb8">Check against the paper</p>
          {lines.map((l) => (
            <button key={l.key} type="button" className="card card-btn" onClick={() => setPicked(l.key)}>
              <div className="flex">
                <p className="t-strong grow">
                  {l.item.name}{Number(l.quantity) > 0 ? ` × ${l.quantity}` : ''}
                </p>
                <Icon name="pencil" />
              </div>
              <p className="t-cap mut mt4">
                {[
                  l.item.batchTracked
                    ? l.lotCode ? `batch ${l.lotCode}` : 'no batch code yet'
                    : 'no batch',
                  l.item.batchTracked ? (l.expiresOn ? `exp ${l.expiresOn.slice(5, 7)}/${l.expiresOn.slice(0, 4)}` : 'needs its date') : null,
                  l.unitCost ? `EGP ${l.unitCost}/${l.item.unit || 'unit'}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            </button>
          ))}
        </>
      ) : (
        <p className="t-sec mut mb8">Nothing on this invoice yet — Add manually puts the first line here.</p>
      )}
      <button type="button" className="btn btn-primary mt12" disabled={!ready || receive.isPending}
        onClick={() => { if (ready && !receive.isPending) receive.mutate(); }}>
        ✓ Receive — into {branchName} stock
      </button>
      {undated.length ? (
        <p className="t-cap amb mt12">
          Expiry is required for batch-tracked lines: {undated.map((l) => l.item.name).join(', ')}.
        </p>
      ) : (
        <p className="t-cap mut mt12">every line is logged against the invoice number</p>
      )}
    </>
  );

  /* THE DELIVERY — the invoice lines as the desk confirms them. The same body at both widths: a quantity or a cost that differed between them would be two versions of what arrived. */
  const receiveBody = (
    <>
          <TabBar
            label="How this delivery starts"
            items={[
              { key: 'blank', label: 'Blank — from an invoice' },
              { key: 'order', label: 'From an order' },
            ]}
            active={way}
            onSelect={setWay}
          />
          {way === 'order' ? orderPicker : scanZone}
          {unmatchedNote}
          {order ? (
            <p className="t-sec brand mb8">
              Pre-filled from #{order.number}
              {' · '}
              <button type="button" className="lnk" onClick={() => go('/stock-manager/orders')}>Open the order</button>
            </p>
          ) : null}
          <Card>
            <div className="flex" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                <p className="t-ovl mut">Supplier</p>
                {suppliers?.length ? (
                  <p className="mt4">
                    <PickerChip
                      label={`${supplier?.name ?? 'Pick a supplier'} ▾`}
                      ariaLabel="Supplier"
                      kind={supplierId ? 'brand' : 'outline'}
                      value={supplierId}
                      onChange={setSupplierId}
                      options={[
                        { value: '', label: 'Not from a listed supplier' },
                        ...suppliers.map((s) => ({
                          value: s.id,
                          label: s.name,
                          hint: s.contactName ?? s.contactPhone ?? undefined,
                        })),
                      ]}
                    />
                  </p>
                ) : (
                  <input
                    className="fld t-strong mt4"
                    aria-label="Supplier"
                    placeholder="who it came from"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                  />
                )}
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 150 }}>
                <label className="t-ovl mut" htmlFor="rcv-ref">Invoice</label>
                <input
                  id="rcv-ref"
                  className="fld t-strong mt4"
                  placeholder="invoice number"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
            </div>
            <p className="t-cap mut mt8">
              The invoice number rides every movement this creates, so a box on the shelf can always
              be traced back to the paper it came on.
            </p>
          </Card>
  
          <DataTable
            caption="The lines on this invoice"
            columns={[
              { key: 'line', label: 'Line', width: 46 },
              { key: 'qty', label: 'Qty', width: 10 },
              { key: 'batch', label: 'Batch', width: 15 },
              { key: 'expiry', label: 'Expiry', width: 14 },
              { key: 'cost', label: 'Unit cost', width: 15 },
            ]}
            rows={tableRows}
            selectedId={pickedKey}
            onSelect={(r) => setPicked(r.id === pickedKey ? null : r.id)}
            empty={
              <div className="emptystate">
                <p className="es-t">Nothing on this invoice yet</p>
                <p className="t-sec mut">
                  Add each line that arrived. Scanning a barcode puts it here too, and jumps to it if
                  it is already on the list.
                </p>
                <p className="mt12">
                  <DChip kind="brand" onClick={() => setAdding(true)}>+ Add a line</DChip>
                </p>
              </div>
            }
          />
  
          {/* WHY THE BUTTON IS DOWN, in the content rather than in a tooltip: a
              greyed primary with no explanation is as dead as one that ignores
              the click. */}
          {uncounted.length ? (
            <p className="t-sec amb mt16">
              {uncounted.length === 1
                ? `How many ${uncounted[0].item.name} arrived?`
                : `${uncounted.length} lines have no quantity yet: ${uncounted
                    .map((l) => l.item.name)
                    .join(', ')}.`}
            </p>
          ) : null}
          <p className="t-sec mut mt16">
            Expiry is REQUIRED for batch-tracked lines — a lot cannot enter the shelf undated.
            {undated.length ? (
              <>
                {' '}
                <span className="amb">
                  {undated.length === 1
                    ? 'One line still needs the date on its box: '
                    : `${undated.length} lines still need the date on their boxes: `}
                  {undated.map((l) => l.item.name).join(', ')}.
                </span>
              </>
            ) : null}
          </p>
    </>
  );

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <StockMenu door="receive" onClose={close} />,
      /* `g_k4` — the same body under a crumb; a phone has no rail
         to return through. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          crumb="Stock"
          title="Receive"
          onBack={() => go('/stock-manager/shelf')}
          menu={(close) => <StockMenu door="receive" onClose={close} />}
        >
          {phoneBody}
        </PhoneScreen>
      ),
      title: 'Receive',
      sub: order
        ? `from order #${order.number} · ${order.supplierName ?? ''}`
        : `into ${branchName} stock`,
      search: 'Scan a barcode to jump to a line',
      searchValue: scan,
      onSearchChange: (v) => {
        setScan(v);
        /* A SCANNER TYPES AND PRESSES ENTER — so a code that matches an item
           either jumps to the line already on the invoice or adds it. */
        const code = v.trim().toLowerCase();
        if (code.length < 4) return;
        const hit = items.find((i) => (i.barcode ?? '').toLowerCase() === code);
        if (!hit) return;
        const existing = lines.find((l) => l.item.id === hit.id);
        if (existing) setPicked(existing.key);
        else addItem(hit);
        setScan('');
      },
      secondary: { label: '+ Add manually', onClick: () => setAdding(true) },
      primary: {
        label: lines.length
          ? `✓ Receive ${lines.length} line${lines.length === 1 ? '' : 's'}`
          : '✓ Receive',
        disabled: !ready || receive.isPending,
        onClick: () => {
          if (ready && !receive.isPending) receive.mutate();
        },
      },
      pane: picked ? (
        <LotPane
          line={picked}
          supplier={supplier}
          supplierName={supplierName}
          reference={reference}
          onPatch={(next) => patch(picked.key, next)}
          onRemove={() => {
            setLines((ls) => ls.filter((l) => l.key !== picked.key));
            setPicked(null);
          }}
          onClose={() => setPicked(null)}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Lot info"
              title={lines.length ? 'Pick a line' : 'Nothing on this invoice yet'}
              sub={
                lines.length
                  ? 'its batch, its date and what it cost are set here'
                  : 'add what arrived, then set each line’s batch and date'
              }
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              Every line becomes a lot on the shelf: a quantity, the code on the box, and the date
              printed on it. The date is what FEFO sorts by, which is why a batch-tracked line
              cannot go in without one.
            </p>
          </div>
        </Pane>
      ),
    },
    [branchName, picked, lines, supplier, supplierName, reference, ready, receive.isPending, scan, items, pickedKey, way, orders, order, suppliers, supplierId],
  );

  return (
    <div className="maie desk stock-root">
      <div className="dcontent scroll">
        {receiveBody}
      </div>

      <AddLineModal
        open={adding}
        items={items}
        already={lines.map((l) => l.item.id)}
        onClose={() => setAdding(false)}
        onPick={addItem}
      />
    </div>
  );
}

/** The docked line — its batch, its date, its note, and where it came from. */
function LotPane({
  line,
  supplier,
  supplierName,
  reference,
  onPatch,
  onRemove,
  onClose,
}: {
  line: Line;
  supplier: Supplier | null;
  supplierName: string;
  reference: string;
  onPatch: (next: Partial<Line>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const qty = Number(line.quantity) || 0;
  const cost = Number(line.unitCost);
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Lot info"
          title={line.item.name}
          sub={
            qty
              ? `${qty} ${line.item.unit || 'units'}${cost ? ` · ${cost.toFixed(2)} each` : ''}`
              : 'how many arrived?'
          }
          wrapTitle
        />
      }
      foot={
        <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onRemove}>
          Take this line off
        </button>
      }
    >
      <Card>
        <p className="t-ovl mut">Supplier</p>
        <p className="t-strong mt8">
          {supplier?.name || supplierName.trim() || 'Not named on this invoice'}
        </p>
        {supplier?.contactName || supplier?.contactPhone ? (
          <p className="t-cap mut mt8">
            {[supplier.contactName, supplier.contactPhone].filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </Card>

      <Card>
        <p className="t-ovl mut">Invoice</p>
        <p className="t-strong mt8">{reference.trim() || 'No number yet'}</p>
        <p className="t-cap mut mt8">it rides every movement this line creates</p>
      </Card>

      {line.item.batchTracked ? (
        <>
          <Card>
            <label className="t-ovl mut" htmlFor="pane-batch">Batch</label>
            <input
              id="pane-batch"
              className="fld t-strong mt4"
              placeholder="the code on the box"
              value={line.lotCode}
              onChange={(e) => onPatch({ lotCode: e.target.value })}
            />
          </Card>
          <Card style={line.expiresOn ? undefined : { borderColor: 'var(--warning-600)' }}>
            <label className="t-ovl mut" htmlFor="pane-exp">Expiry</label>
            <input
              id="pane-exp"
              className="fld t-strong mt4"
              type="date"
              value={line.expiresOn}
              onChange={(e) => onPatch({ expiresOn: e.target.value })}
            />
            <p className="t-cap mut mt8">
              {line.expiresOn
                ? `${expiryLabel(line.expiresOn)} — FEFO will reach for this box ${
                    line.item.lots.some(
                      (l) => l.expiresOn && line.expiresOn && l.expiresOn < line.expiresOn,
                    )
                      ? 'after the ones already dated sooner'
                      : 'first'
                  }`
                : 'required — this line is batch-tracked'}
            </p>
          </Card>
        </>
      ) : (
        <Card>
          <p className="t-ovl mut">Batch</p>
          <p className="t-strong mt8">Not tracked</p>
          <p className="t-cap mut mt8">
            this item is one number on the shelf, with no boxes to date — {line.item.name} is
            counted, not traced
          </p>
        </Card>
      )}

      <Card>
        <label className="t-ovl mut" htmlFor="pane-cost">Unit cost</label>
        <input
          id="pane-cost"
          className="fld t-strong mt4 num"
          inputMode="decimal"
          placeholder="cost per unit"
          value={line.unitCost}
          onChange={(e) => onPatch({ unitCost: e.target.value })}
        />
        {qty && cost ? (
          <p className="t-cap mut mt8">{(qty * cost).toFixed(2)} for this line</p>
        ) : null}
      </Card>

      <Card>
        <label className="t-ovl mut" htmlFor="pane-note">Note on this lot</label>
        <input
          id="pane-note"
          className="fld t-strong mt4"
          placeholder="anything that rides this batch"
          value={line.note}
          onChange={(e) => onPatch({ note: e.target.value })}
        />
      </Card>
      <Card kind="accent">
        <p className="t-sec brand">
          The note rides the movement — it shows on the item’s history, beside the receipt that
          brought the box in.
        </p>
      </Card>
    </Pane>
  );
}

/** Which item arrived — the shelf's own list, searched by name or barcode. */
function AddLineModal({
  open,
  items,
  already,
  onClose,
  onPick,
}: {
  open: boolean;
  items: ShelfItem[];
  already: string[];
  onClose: () => void;
  onPick: (item: ShelfItem) => void;
}) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const hits = items
    .filter((i) => !needle || `${i.name} ${i.barcode ?? ''}`.toLowerCase().includes(needle))
    .slice(0, 40);

  return (
    <Modal open={open} onClose={onClose} title="Add a line" sub="what arrived on this invoice">
      <input
        className="fld"
        autoFocus
        placeholder="Name or barcode"
        aria-label="Find an item"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="mt12" style={{ maxHeight: 320, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <p className="t-sec mut">
            The catalog is empty — an invoice can only be received against something the clinic
            says it buys.
          </p>
        ) : hits.length === 0 ? (
          <p className="t-sec mut">Nothing in the catalog matches “{q.trim()}”.</p>
        ) : (
          hits.map((i) => (
            <button
              key={i.id}
              type="button"
              className="card w-full text-start"
              onClick={() => onPick(i)}
            >
              <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <p className="t-strong">{i.name}</p>
                  <p className="t-cap mut">
                    {i.unit || i.category || 'item'}
                    {i.batchTracked ? ' · batch-tracked' : ' · no batches'}
                    {i.barcode ? ` · ${i.barcode}` : ''}
                  </p>
                </div>
                {already.includes(i.id) ? <Chip kind="soft" small>on this invoice</Chip> : null}
                <span className="t-sec mut">{i.inStock}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
